<template>
  <CreateBase
    :current-step="3"
    :title="$t('Invite contractors')"
    v-on="$listeners"
    @click.native="closeContractorsSearch"
  >
    <div class="search-contractors-container">
      <FormInput
        id="search-contractors"
        :placeholder="$t('Search for existing Contractors')"
        :icon="searchIcon"
        :value="searchText"
        @focus="searchContractors(searchText)"
        @input="searchContractors"
      >
        <template v-if="contractors.length" #autocomplete>
          <UserSearchResultsContainer>
            <div v-for="team in contractors" :key="`${team.team_id}-team`">
              <MarkText
                class="search-contractors__company"
                :text="team.name"
                :substring="searchText"
              />
              <div class="search-contractors__company-result">
                <UserSearchResultItem
                  v-for="member in team.contacts"
                  :key="`${member.id}-user`"
                  @click="
                    handleContractorChecking({
                      ...member,
                      company_name: team.team_name,
                    })
                  "
                >
                  <Checkbox
                    class="search-contractors__contractor__checkbox"
                    :checked="isChecked(member)"
                    @check="handleContractorChecking(member, team)"
                  />
                  <Contractor :contractor="member" :search-contractor="searchText" />
                </UserSearchResultItem>
              </div>
            </div>
          </UserSearchResultsContainer>
        </template>
      </FormInput>

      <div class="spacey-12">
        <Contractors
          :contractors="allContractors"
          @edit="(idx) => (editContractorHelper = { ...tender.contractors[idx], idx })"
          @delete="deleteContractor"
        />

        <Button
          icon="plus"
          type="secondary"
          size="small"
          class="mt-12"
          @click.native="openAddNewContractorModal"
        >
          {{ $t('Add new contractor') }}
        </Button>
      </div>
    </div>

    <template #buttons>
      <Button type="secondary" action-type="button" class="mr-8" @click="$emit('change-step', 2)">
        {{ $t('Back') }}
      </Button>
      <Button :loading="saving" @click="createTender">
        {{ editId ? $t('Save tender') : $t('Create tender') }}
      </Button>
      <ErrorDialog v-model="error" />
    </template>
  </CreateBase>
</template>

<script>
  import CreateBase from '@/containers/tenders/CreateBase';
  import Button from '@/components/ui-v2/Button';
  import FormInput from '@/components/ui/FormInput';
  import SearchIcon from '@/assets/icons/search.svg';
  import Contractors from '@/containers/tenders/create/tender-contractors/TenderContractors';
  import Checkbox from '@/components/ui/Checkbox';
  import Contractor from '@/components/info/Contractor';
  import MarkText from '@/components/ui/MarkText';
  import request from '@/utils/api/request';
  import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
  import get from 'lodash.get';
  import ErrorDialog from '@/components/ui/ErrorDialog';
  import ClickOutside from 'vue-click-outside';
  import UserSearchResultItem from '@/components/common/UserSearchResults/UserSearchResultItem';
  import UserSearchResultsContainer from '@/components/common/UserSearchResults/UserSearchResultsContainer';
  import AddContractorsModal from '@/containers/contractors/add-contractors-modal/AddContractorsModal';
  import parseError from '@/utils/formatting/parse-error';

  export default {
    name: 'Invite',
    components: {
      UserSearchResultsContainer,
      CreateBase,
      Button,
      FormInput,
      Contractors,
      Checkbox,
      Contractor,
      MarkText,
      ErrorDialog,
      UserSearchResultItem,
    },
    directives: { ClickOutside },
    data() {
      return {
        saving: false,
        editContractorHelper: null,
        searchText: '',
        searchIcon: SearchIcon,
        contractors: [],
        selectedContractors: [],
        error: false,
      };
    },
    computed: {
      ...mapState({
        tender: (state) => state.tenderEdit.tender,
      }),
      ...mapGetters('tenderEdit', ['editId']),
      allContractors() {
        return get(this.tender, 'offers', []);
      },
    },
    methods: {
      get,
      ...mapActions({
        updateTenders: 'projectTenders/loadTenders',
        createTender: 'tenderEdit/createTender',
        close: 'modal/closeModal',
        openModal: 'modal/openModal',
        loadTenderCompanies: 'tender/loadTenderCompanies',
        addContractor: 'tenderEdit/addContractor',
      }),
      ...mapMutations('tenderEdit', [
        'changeTender',
        'addContractor',
        'deleteContractor',
        'editContractor',
        'deleteContractorById',
      ]),
      handleContractorAddition(contractor) {
        this.addContractor(contractor);
      },
      handleContractorEditing(contractor) {
        this.editContractor({
          contractor,
          idx: this.editContractorHelper.idx,
        });
        this.editContractorHelper = null;
      },
      cancelEditing(state) {
        if (!state && this.editContractorHelper) this.editContractorHelper = null;
      },
      openAddNewContractorModal() {
        this.openModal({
          component: AddContractorsModal,
          context: {
            isTenderContractor: true,
          },
          addContractor: (contractor) => {
            this.addContractor(contractor);
          },
          isNested: true,
        });
      },
      async createTender() {
        this.saving = true;
        try {
          if (this.editId) {
            await request.put(`/tenders/${this.editId}/`, {
              name: this.tender.name,
              description: this.tender.description,
              address: this.tender.address,
              offers_deadline_date: this.tender.offers_deadline_date,
              public_budget: this.tender.public_budget,
              internal_budget: this.tender.internal_budget,
              row_ids: this.tender.row_ids,
              project_id: this.tender.project_id,
              file_ids: this.tender.file_ids,
              contact_person_responsible_user_id:
                this.tender.contact_person_responsible_user_id ||
                this.tender.contact_person_responsible_user.id,
              offers: this.addContractors(this.tender.offers),
              taxes: this.tender.taxes,
            });
          } else {
            await request.post('/tenders/', {
              ...this.tender,
              is_advanced_pricing_enabled: true,
              project_id: this.$route.params.id,
              offers: this.addContractors(this.tender.offers),
            });
          }
          this.close();
          await this.updateTenders({ projectId: this.$route.params.id });
          // Скрываем заглушку и показываем вновь добавленные компании
          if (this.$route.params.tenderId) {
            await this.loadTenderCompanies();
          }
        } catch (error) {
          this.error = parseError(error, {
            returnDetail: true,
          });
        }
        this.saving = false;
      },
      async searchContractors(text = '') {
        this.searchText = text;
        try {
          const { data } = await request.get('/contractors/search/', {
            params: {
              text,
            },
          });
          this.contractors = data;
        } catch (e) {
          console.error(e);
        }
      },
      closeContractorsSearch() {
        this.contractors = [];
      },
      isChecked(member) {
        return this.allContractors.some((item) => {
          if (item.contractor_responsible_users) {
            return Object.values(item.contractor_responsible_users)?.some(
              (elem) => elem.user_id === member.user_id
            );
          }
          return false;
        });
      },

      handleContractorChecking(contractor, team) {
        const computedContractor = {
          contractor_responsible_users: [contractor],
          contractor_team: {
            name: team.name,
            id: team.id,
          },
        };
        if (!this.isChecked(contractor)) {
          this.addContractor(computedContractor);
        } else {
          this.deleteContractorById({ teamId: team.id, contractorId: contractor.id });
        }
      },
      addContractors(contractors) {
        return contractors.map((contractor) => {
          const body = {};
          if (contractor.id) {
            body.id = contractor.id;
          } else {
            body.cdb_team_id = contractor.contractor_team?.id;
            body.cdb_contact_ids = contractor.contractor_responsible_users?.map(
              (contact) => contact.id
            );
          }
          return body;
        });
      },
    },
  };
</script>
